import { blocksToMd, getTextFromChars, type Block, type Blocks } from '@w/content-blocks'
import type { Tel } from '@w/tel'
import { blobToBase64, type UFile } from '@w/utils'
import type { QuestionAnswerValue } from '@w/content-blocks-editor'
import { gradeClozeAnswer } from '../pages/training/cloze-answer-grading'
import type { GradeClozeInput } from '../server/endpoints/grading'
import type { FlashcardsFeRouterCtx } from '../router/flashcards-fe.router-ctx'
import { canSubmitTrainingBlock, scoreTrainingBlock, type ActiveTrainingBlock, type TrainingViewerState } from './training-scoring'

/** The model graders a question may call; training and the page reader pass the same RPCs. */
export type QuestionGraders = {
  gradeCloze: (input: GradeClozeInput, tel: Tel) => Promise<{ correct: boolean; feedback: string }>
  gradeLa: (input: { question: string; criteria: string[]; referenceAnswer: string; userAnswer: string }, tel: Tel) => Promise<{ correct: boolean; feedback: string }>
}

/** The app's RPCs as graders. */
export function graders(rpc: FlashcardsFeRouterCtx['rpcClient']): QuestionGraders {
  return {
    gradeCloze: (input, tel) => rpc.gradeCloze.mutate(input, { tel }),
    gradeLa: (input, tel) => rpc.gradeLa.mutate(input, { tel }),
  }
}

/** A voice answer as text, through the same transcription service training uses. */
export async function transcribeAnswer(rpc: FlashcardsFeRouterCtx['rpcClient'], recording: UFile, tel: Tel): Promise<string> {
  const transcript = await rpc.transcribeTrainingAudio.mutate({
    dataBase64: await blobToBase64(recording.blob),
    mimeType: recording.type.split(';')[0] ?? recording.type,
  }, { tel })
  return transcript.text
}

/** The editor's answer for one part, as the graders read it. */
export function answerViewerState(answer: QuestionAnswerValue): TrainingViewerState {
  return {
    textValue: answer.text ?? '',
    audio: answer.audio?.blob,
    selectedIndex: answer.selectedIndex ?? null,
    selectedIndices: answer.selectedIndices ?? [],
  }
}

export type QuestionGrade = { score: number; partMarks: number[]; feedback: string | null }

/**
 * One qid = one question; each part is graded on its own and the question is right only when every part is
 * right (mc-like). `excerpt` is the top-level blocks holding the question: the context a model grader reads.
 */
export async function gradeQuestion(input: {
  parts: readonly ActiveTrainingBlock[]
  states: readonly TrainingViewerState[]
  excerpt: Blocks
  graders: QuestionGraders
  tel: Tel
}): Promise<QuestionGrade> {
  const grades = await Promise.all(input.parts.map((part, index) =>
    _gradePart(part, input.states[index] ?? { textValue: '', selectedIndex: null, selectedIndices: [] }, input)))
  const feedbacks = grades.flatMap(grade => grade.feedback === null ? [] : [grade.feedback])
  return {
    score: grades.every(grade => grade.score === 1) ? 1 : 0,
    partMarks: grades.map(grade => grade.score),
    feedback: feedbacks.length > 0 ? feedbacks.join(' ') : null,
  }
}

// One part's grade: an unanswered part is wrong (mc-like); exact/regexp first,
// AI only for a non-empty free-text answer.
async function _gradePart(part: ActiveTrainingBlock, state: TrainingViewerState, input: { excerpt: Blocks; graders: QuestionGraders; tel: Tel }): Promise<{ score: number; feedback: string | null }> {
  const text = state.textValue
  if (part.type === 'cloze') {
    if (!canSubmitTrainingBlock(part, state)) return { score: 0, feedback: null }
    const grade = await gradeClozeAnswer({
      blank: { answer: blocksToMd(part.children), answerRegexp: part.answerRegexp },
      clozeMarkdown: blocksToMd(input.excerpt),
      userAnswer: text,
    }, async (clozeInput) => {
      const response = await input.graders.gradeCloze(clozeInput, input.tel)
      return { correct: response.correct, reason: response.feedback }
    })
    if (grade.method === 'failed') throw new Error(grade.reason)
    return { score: grade.correct ? 1 : 0, feedback: grade.method === 'model' ? grade.reason : grade.correct ? 'Correct.' : 'Incorrect.' }
  }
  if (part.subtype === 'la') {
    // Missing author criteria is a card error, never a learner's wrong answer.
    if (!part.criteria.some(criterion => criterion.trim())) throw new Error('This card needs grading criteria before AI grading.')
    if (!canSubmitTrainingBlock(part, state)) return { score: 0, feedback: null }
    const grade = await input.graders.gradeLa({
      question: plainText(part.question ?? []),
      criteria: part.criteria,
      referenceAnswer: plainText(part.explanation ?? []),
      userAnswer: text,
    }, input.tel)
    return { score: grade.correct ? 1 : 0, feedback: grade.feedback }
  }
  if (!canSubmitTrainingBlock(part, state)) return { score: 0, feedback: null }
  const result = scoreTrainingBlock(part, state)
  // sr parts are self-scored after reveal and never reach submit.
  if (!result) throw new Error(`Part ${part.id} cannot be auto-graded`)
  return { score: result.score, feedback: null }
}

function plainText(blocks: Blocks): string {
  return blocks.map((block) => _plainTextBlock(block)).join('')
}

function _plainTextBlock(block: Block): string {
  switch (block.type) {
    case 'text':
      return block.text
    case 'chars':
      return getTextFromChars(block)
    case 'br':
      return '\n'
    case 'question':
      return block.question ? plainText(block.question) : ''
    case 'pageTitle':
      return block.title
    case 'cloze':
      return plainText(block.children)
    case 'code':
    case 'l':
    case 'em':
    case 'a':
    case 'h':
    case 'p':
    case 'quote':
    case 'alert':
    case 'rule':
    case 'questionGroup':
    case 'list':
    case 'li':
    case 'pre':
    case 'root':
    case 'td':
    case 'tr':
    case 'definition':
      return plainText(block.children)
    case 'comment':
    case 'comments':
    case 'criticComment':
    case 'diff':
    case 'review':
    case 'img':
    case 'video':
    case 'audio':
    case 'file':
    case 'preview':
    case 'pages':
    case 'page':
    case 'pageLink':
    case 'pageMeta':
    case 'cols':
    case 'col':
    case 'table':
    case 'hr':
    case 'mention':
      return ''
  }

  return ''
}
